import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatINR, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Car, CircleDot, CalendarCheck, CheckCircle2, Truck, Wallet, Landmark } from "lucide-react";
import DashboardCharts from "@/components/DashboardCharts";
const statusMeta = {
  available: { label: "Available", color: "#16A34A" },
  booked: { label: "Booked", color: "#D97706" },
  sold: { label: "Sold", color: "#2563EB" },
  delivered: { label: "Delivered", color: "#475569" },
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/dashboard/charts").then((r) => setCharts(r.data)).catch(() => {});
  }, []);

  if (!stats) return <div className="text-slate-400 font-semibold">Loading…</div>;

  const c = stats.counts;
  const totalInStock = (c.available || 0) + (c.booked || 0);
  const tiles = [
    { key: "all", label: "Total Cars", value: totalInStock, icon: Car, color: "#1E3A8A" },
    { key: "available", label: "Available", value: c.available || 0, icon: CircleDot, color: "#16A34A" },
    { key: "booked", label: "Booked", value: c.booked || 0, icon: CalendarCheck, color: "#D97706" },
    { key: "sold", label: "Sold", value: c.sold || 0, icon: CheckCircle2, color: "#2563EB" },
    { key: "delivered", label: "Delivered", value: c.delivered || 0, icon: Truck, color: "#475569" },
  ];

  return (
    <div className="a1-fade">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-head font-black text-3xl sm:text-4xl tracking-tight">Good day, {user?.name}</h1>
          <p className="text-slate-500 font-semibold">Today • {formatDate(new Date())}</p>
        </div>
        <button data-testid="dash-new-intake" onClick={() => navigate("/intake")} className="a1-btn a1-btn-action">
          + Add New Car
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6 mb-6">
        {tiles.map((t) => (
          <button
            key={t.key}
            data-testid={`tile-${t.key}`}
            onClick={() => navigate(`/inventory?status=${t.key}`)}
            className="a1-card p-5 sm:p-6 text-left hover:border-slate-300 transition-colors"
          >
            <t.icon className="w-8 h-8 mb-3" style={{ color: t.color }} strokeWidth={2.5} />
            <div className="num font-head font-black text-4xl tracking-tight" style={{ color: t.color }}>
              {t.value}
            </div>
            <div className="text-sm font-bold text-slate-500 mt-1">{t.label}</div>
            {t.key === "all" && (
              <div className="text-[11px] font-semibold text-slate-400 mt-0.5 leading-tight">Available + Booked</div>
            )}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-6">
        <div className="a1-card p-6">
          <div className="flex items-center gap-2 text-slate-500 font-bold text-sm uppercase tracking-widest mb-2">
            <Wallet className="w-5 h-5" /> Cash in Hand (Today)
          </div>
          <div className="num font-head font-black text-4xl tracking-tight text-[#0F172A]">{formatINR(stats.cash)}</div>
        </div>
        <div className="a1-card p-6">
          <div className="flex items-center gap-2 text-slate-500 font-bold text-sm uppercase tracking-widest mb-2">
            <Landmark className="w-5 h-5" /> Bank Balance (Today)
          </div>
          <div className="num font-head font-black text-4xl tracking-tight text-[#0F172A]">{formatINR(stats.bank)}</div>
        </div>
      </div>

      <h2 className="font-head font-extrabold text-2xl tracking-tight mb-3">Business Overview</h2>
      <DashboardCharts data={charts} />

      <h2 className="font-head font-extrabold text-2xl tracking-tight mb-3">Recent Cars</h2>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {stats.recent.length === 0 && <p className="text-slate-400 font-semibold">No cars yet. Add your first car.</p>}
        {stats.recent.map((v) => {
          const s = statusMeta[v.status] || statusMeta.available;
          return (
            <button
              key={v.id}
              onClick={() => navigate(`/vehicle/${v.id}`)}
              data-testid={`recent-${v.id}`}
              className="a1-card p-5 text-left hover:border-slate-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <span className="font-head font-extrabold text-lg tracking-tight">{v.model || "—"}</span>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: s.color }}>
                  {s.label}
                </span>
              </div>
              <div className="text-slate-500 font-bold num">{v.reg_no}</div>
              <div className="text-sm text-slate-400 font-semibold mt-1">Inward {formatDate(v.inward_date)}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
